import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Briefcase, Loader2, UserPlus } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { clientApi } from '@/lib/adminApi';
import { toast } from 'sonner';

/**
 * TalentCheck Client Autonomy (P0 remediation) — single-step autonomous
 * process creation. The Client Admin owns the ordinary lifecycle:
 * create → position profile → start → (optional) first candidate.
 * No CTH approval is involved at any point.
 */
export function ClientTalentCheckCreateDialog({ open, onOpenChange, onCreated }) {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const navigate = useNavigate();

  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [formalPositionId, setFormalPositionId] = useState('');
  const [busy, setBusy] = useState(false);

  // Position / vacancy profile
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [responsibilities, setResponsibilities] = useState('');
  const [openings, setOpenings] = useState(1);
  const [urgency, setUrgency] = useState('medium');
  const [modality, setModality] = useState('onsite');

  // Optional first candidate
  const [candName, setCandName] = useState('');
  const [candEmail, setCandEmail] = useState('');
  const [candPhone, setCandPhone] = useState('');
  const [candCvUrl, setCandCvUrl] = useState('');

  // Client-owned process notes
  const [processNotes, setProcessNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    clientApi.listDepartments()
      .then((d) => setDepartments(Array.isArray(d) ? d : []))
      .catch(() => setDepartments([]));
  }, [open]);

  // Founder 2026-06 — load formal positions of the chosen department
  // (available slots only). Reference-only; nothing is created.
  useEffect(() => {
    setFormalPositionId('');
    const dept = departments.find((d) => d.name === department);
    if (!open || !dept) { setPositions([]); return; }
    api.get('/client/positions', { params: { department_id: dept.id } })
      .then((r) => setPositions((r.data || []).filter(
        (p) => !p.archived && (!p.headcount || (p.occupied || 0) < p.headcount))))
      .catch(() => setPositions([]));
    // eslint-disable-next-line
  }, [open, department]);

  const reset = () => {
    setTitle(''); setDepartment(''); setResponsibilities('');
    setFormalPositionId(''); setPositions([]);
    setOpenings(1); setUrgency('medium'); setModality('onsite');
    setCandName(''); setCandEmail(''); setCandPhone(''); setCandCvUrl('');
    setProcessNotes('');
  };

  const hasCandidateInput = !!(candName.trim() || candEmail.trim() || candPhone.trim() || candCvUrl.trim());

  const validate = () => {
    if (title.trim().length < 2) {
      toast.error(L('Indica el título del puesto.', 'Provide the position title.'));
      return false;
    }
    if (responsibilities.trim().length < 4) {
      toast.error(L('Describe las responsabilidades clave del puesto.', 'Describe the key responsibilities of the position.'));
      return false;
    }
    if (hasCandidateInput && (candName.trim().length < 2 || !candEmail.includes('@'))) {
      toast.error(L('Para agregar el candidato, indica nombre y correo válidos.', 'To add the candidate, provide a valid name and email.'));
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    let proc = null;
    try {
      const res = await api.post('/admin/recruitment-processes', {
        position_title: title.trim(),
        department: department.trim() || null,
        formal_position_id: formalPositionId || null,
        process_notes: processNotes.trim() || null,
      });
      proc = res.data;
      await api.post(`/client/recruitment-processes/${proc.id}/intake`, {
        position_title: title.trim(),
        department: department.trim() || L('General', 'General'),
        key_responsibilities: responsibilities.trim(),
        urgency,
        work_modality: modality,
        number_of_openings: Number(openings) || 1,
      });
      await api.post(`/admin/recruitment-processes/${proc.id}/approve`);
      if (hasCandidateInput) {
        await api.post(`/admin/recruitment-processes/${proc.id}/candidates`, {
          name: candName.trim(),
          email: candEmail.trim(),
          phone: candPhone.trim() || null,
          source: 'client_referral',
          cv_url: candCvUrl.trim() || null,
        });
        toast.success(L('Proceso iniciado — el candidato ya está en tu pipeline.',
          'Process started — the candidate is already in your pipeline.'));
      } else {
        toast.success(L('Proceso iniciado. Ya puedes agregar candidatos y asignar evaluaciones.',
          'Process started. You can now add candidates and assign assessments.'));
      }
      reset();
      onOpenChange?.(false);
      onCreated?.(proc);
      navigate(`/client/recruitment/${proc.id}`);
    } catch (e) {
      if (proc) {
        toast.error(L('El proceso fue creado pero un paso no se completó. Puedes continuarlo desde el detalle del proceso.',
          'The process was created but one step did not complete. You can continue it from the process detail.'), {
          description: formatApiError(e),
        });
        onOpenChange?.(false);
        onCreated?.(proc);
        navigate(`/client/recruitment/${proc.id}`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { if (!o) reset(); onOpenChange?.(o); } }}>
      <DialogContent className="w-[95vw] sm:max-w-3xl lg:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="tc-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" />
            {L('Crear proceso de selección', 'Create recruitment process')}
          </DialogTitle>
          <DialogDescription>
            {L('Tu organización gestiona este proceso de forma autónoma: se inicia de inmediato, sin aprobación de CTH. Puedes solicitar apoyo profesional cuando lo desees.',
               'Your organization runs this process autonomously: it starts immediately, with no CTH approval. You can request professional support anytime.')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 md:grid-cols-2">
          {/* LEFT — position / vacancy profile */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {L('Perfil del puesto', 'Position profile')}
            </p>
            <div className="space-y-1">
              <Label htmlFor="tcc-title">
                {L('Título del puesto', 'Position title')} <span className="text-destructive">*</span>
              </Label>
              <Input id="tcc-title" value={title} onChange={(e) => setTitle(e.target.value)}
                     placeholder={L('Ingeniero Backend Senior', 'Senior Backend Engineer')}
                     data-testid="tc-create-title" />
            </div>
            <div className="space-y-1">
              <Label>{L('Departamento', 'Department')}</Label>
              <Select
                value={department || '__none__'}
                onValueChange={(v) => setDepartment(v === '__none__' ? '' : v)}
              >
                <SelectTrigger data-testid="tc-create-department">
                  <SelectValue placeholder={L('Selecciona un departamento', 'Select a department')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{L('— Sin especificar', '— Not specified')}</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Founder 2026-06 — OPTIONAL link to an existing formal position.
                 Reference only: never creates positions/vacancies. */}
            {department && positions.length > 0 && (
              <div className="space-y-1">
                <Label>{L('Puesto formal / plaza vacante (opcional)', 'Formal position / vacant slot (optional)')}</Label>
                <Select
                  value={formalPositionId || '__none__'}
                  onValueChange={(v) => setFormalPositionId(v === '__none__' ? '' : v)}
                >
                  <SelectTrigger data-testid="tc-create-formal-position">
                    <SelectValue placeholder={L('— Sin puesto formal', '— No formal position')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{L('— Sin puesto formal', '— No formal position')}</SelectItem>
                    {positions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.title}{p.headcount ? ` · ${p.occupied || 0}/${p.headcount} ${L('plazas', 'slots')}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {L('Vincular no crea puestos ni plazas nuevas; solo referencia la estructura existente.',
                     'Linking never creates new positions or slots; it only references the existing structure.')}
                </p>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="tcc-resp">
                {L('Responsabilidades clave', 'Key responsibilities')} <span className="text-destructive">*</span>
              </Label>
              <Textarea id="tcc-resp" rows={4} maxLength={4000} value={responsibilities}
                        onChange={(e) => setResponsibilities(e.target.value)}
                        placeholder={L('Describe las funciones principales del puesto…', 'Describe the main duties of the position…')}
                        data-testid="tc-create-responsibilities" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="tcc-openings">{L('N.º de vacantes', 'Openings')}</Label>
                <Input id="tcc-openings" type="number" min={1} max={500} value={openings}
                       onChange={(e) => setOpenings(e.target.value)} data-testid="tc-create-openings" />
              </div>
              <div className="space-y-1">
                <Label>{L('Urgencia', 'Urgency')}</Label>
                <Select value={urgency} onValueChange={setUrgency}>
                  <SelectTrigger data-testid="tc-create-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{L('Baja', 'Low')}</SelectItem>
                    <SelectItem value="medium">{L('Media', 'Medium')}</SelectItem>
                    <SelectItem value="high">{L('Alta', 'High')}</SelectItem>
                    <SelectItem value="critical">{L('Crítica', 'Critical')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>{L('Modalidad de trabajo', 'Work modality')}</Label>
              <Select value={modality} onValueChange={setModality}>
                <SelectTrigger data-testid="tc-create-modality"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="onsite">{L('Presencial', 'On-site')}</SelectItem>
                  <SelectItem value="hybrid">{L('Híbrida', 'Hybrid')}</SelectItem>
                  <SelectItem value="remote">{L('Remota', 'Remote')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* RIGHT — optional first candidate + process notes */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <UserPlus className="h-3.5 w-3.5" />
              {L('Candidato inicial (opcional)', 'Initial candidate (optional)')}
            </p>
            <div className="space-y-1">
              <Label htmlFor="tcc-cand-name">{L('Nombre del candidato', 'Candidate name')}</Label>
              <Input id="tcc-cand-name" value={candName} onChange={(e) => setCandName(e.target.value)}
                     placeholder={L('Juan Pérez', 'Jane Doe')} data-testid="tc-create-candidate-name" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tcc-cand-email">{L('Correo del candidato', 'Candidate email')}</Label>
              <Input id="tcc-cand-email" type="email" value={candEmail}
                     onChange={(e) => setCandEmail(e.target.value)}
                     placeholder="candidato@ejemplo.com" data-testid="tc-create-candidate-email" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tcc-cand-phone">{L('Teléfono (opcional)', 'Phone (optional)')}</Label>
              <Input id="tcc-cand-phone" value={candPhone} onChange={(e) => setCandPhone(e.target.value)}
                     placeholder="+507 …" data-testid="tc-create-candidate-phone" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tcc-cand-cv">{L('Enlace al CV (opcional)', 'CV link (optional)')}</Label>
              <Input id="tcc-cand-cv" value={candCvUrl} onChange={(e) => setCandCvUrl(e.target.value)}
                     placeholder="https://…" data-testid="tc-create-candidate-cv" />
              <p className="text-xs text-muted-foreground">
                {L('Referencia a un CV alojado externamente. CTH no almacena el documento.',
                   'Reference to an externally hosted CV. CTH does not store the document.')}
              </p>
            </div>
            <div className="space-y-1 pt-2 border-t border-border">
              <Label htmlFor="tcc-notes">{L('Notas del proceso (opcional)', 'Process notes (optional)')}</Label>
              <Textarea id="tcc-notes" rows={3} maxLength={2000} value={processNotes}
                        onChange={(e) => setProcessNotes(e.target.value)}
                        placeholder={L('Notas internas de tu organización sobre este proceso…', 'Your organization’s internal notes about this process…')}
                        data-testid="tc-create-notes" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={busy} data-testid="tc-create-cancel">
            {L('Cancelar', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="tc-create-submit">
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {L('Crear e iniciar proceso', 'Create & start process')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ClientTalentCheckCreateDialog;
